import {
  bpsToFixed,
  clampCue,
  DEFAULT_CUE,
  quantizeAim,
  shotPowerBpsFor,
  spinDecayFor,
  type CueParams,
} from './cue'
import * as F from './fixed'
import type { Fixed } from './fixed'
import { hashEvents, hashState } from './hash'
import { createBall, simularContando, type CollisionEvent, type TableState } from './sim'
import * as T from './table'
import * as V from './vec'

/**
 * Montagem da mesa e execução de tacadas.
 *
 * É a fronteira da engine: o resto do sistema fala com estas funções e nunca
 * mexe em bola diretamente. Tudo aqui é determinístico — sem relógio, sem
 * aleatório, sem estado global.
 */

/** Índice fixo da bola branca. As numeradas são 1 a 15. */
export const CUE_BALL = 0

/**
 * Intenção de tacada — o ÚNICO dado que o cliente envia.
 *
 * O cliente nunca informa onde as bolas pararam; manda o vetor e o servidor
 * calcula o resto (docs/TDD.md §4.3).
 */
export type ShotIntent = {
  /** Direção, em radianos, já em ponto fixo. */
  angle: Fixed
  /** Força de 0 a 1, em ponto fixo. */
  power: Fixed
  /**
   * Ponto de contato do taco na branca, de -1 a 1 em cada eixo.
   * `x` lateral (inglês), `y` vertical (positivo corre, negativo recua).
   */
  spin?: { x: Fixed; y: Fixed }
}

/**
 * Tacada completa: a intenção do jogador mais o taco usado.
 *
 * O taco entra aqui, e não na intenção, porque quem o determina é o servidor a
 * partir do NFT que a carteira possui. Se viesse junto da intenção, seria
 * campo controlado pelo cliente — e taco lendário de graça.
 */
export type Shot = {
  intent: ShotIntent
  cue?: CueParams
  /** Quebra: habilita o bônus de potência do taco. O servidor decide, não o cliente. */
  isBreak?: boolean
}

/** Velocidade da branca com a força no máximo e taco padrão. */
export const MAX_SHOT_SPEED: Fixed = F.from(12)

/**
 * Efeito LATERAL máximo que um taco padrão imprime, em m/s de superfície.
 *
 * Só vale para o inglês (`spinZ`). Foi calibrado contra o simulador de
 * referência para o efeito na tabela e o throw, e por isso ficou como estava
 * na v5, quando o efeito vertical mudou de escala.
 */
export const MAX_SPIN: Fixed = F.from(2.5)

/**
 * Quanto giro vertical um deslocamento do taco imprime, por raio de contato.
 *
 * É a física da esfera, não calibração: bater a `b` raios do centro imprime
 * `ω·R = (5/2)·b·v`, porque o momento de inércia é ⅖mR². No limite de falha
 * de taco (b = 0,5) a superfície gira a 1,25 vezes a velocidade da bola —
 * mais que o rolamento natural, e é o que faz o recuo durar até o alvo.
 *
 * Até a v4 o giro era um valor ABSOLUTO (`MAX_SPIN`), o mesmo 2,5 m/s para
 * uma tacada de 3 m/s e uma de 12. Numa tacada normal isso dava 10% da
 * velocidade, o pano comia em 18 cm, e a branca chegava ao alvo rolando para
 * a frente por mais que o jogador batesse embaixo. O recuo só existia em
 * tacada de quebra com o alvo colado.
 */
export const SPIN_PER_RADIUS: Fixed = F.from(2.5)

/**
 * Até onde do centro o taco ainda pega a bola, em raios — a falha de taco.
 *
 * Passado cerca de meio raio a ponta escorrega, e é por isso que giz existe.
 * O limite mora AQUI, na física, e não só no controle da tela: o replay
 * carrega o contato num i8 que vai a ±1, e um cliente modificado que mandasse
 * 1,0 ganharia o dobro do recuo que qualquer taco de verdade dá. O controle
 * (`apps/web`) importa este valor para desenhar o mesmo círculo.
 */
export const MISCUE_LIMIT: Fixed = F.from(0.5)

/**
 * Monta o triângulo de 15 bolas no ponto de pé.
 *
 * O `jitter` é o deslocamento sub-milimétrico derivado do commit-reveal dos
 * dois jogadores (TDD §4.4). Sem ele a quebra vira um problema resolvido:
 * alguém acha o vetor ótimo e ganha toda partida em que quebra.
 */
export function rackBalls(jitter: readonly Fixed[] = []): TableState {
  const balls = [createBall(CUE_BALL, T.CUE_SPOT.x, T.CUE_SPOT.y)]

  const passo = T.CONTACT_DISTANCE + RACK_GAP
  const alturaLinha = F.mul(passo, F.from(0.866)) // sen(60°)

  let id = 1
  for (let linha = 0; linha < 5; linha++) {
    const x = T.FOOT_SPOT.x + F.mul(alturaLinha, F.fromInt(linha))
    const yBase = T.FOOT_SPOT.y - F.mul(F.div(passo, F.fromInt(2)), F.fromInt(linha))

    for (let coluna = 0; coluna <= linha; coluna++) {
      const y = yBase + F.mul(passo, F.fromInt(coluna))

      const dx = jitter[(id - 1) * 2] ?? 0
      const dy = jitter[(id - 1) * 2 + 1] ?? 0

      balls.push(createBall(id, x + dx, y + dy))
      id++
    }
  }
  return { balls }
}

/**
 * Folga entre as bolas do triângulo, antes do jitter.
 *
 * Existe para as bolas não nascerem em contato, o que dispararia colisões
 * antes da primeira tacada. É este número que limita o quanto cada bola pode
 * ser deslocada — ver `BALL_JITTER`.
 */
export const RACK_GAP: Fixed = F.from(0.0005)

/**
 * Deslocamento do triângulo inteiro, em cada eixo. Física v2.
 *
 * ±2 mm é o que dá para extrair de um byte com resolução útil: em Q16.16 uma
 * unidade vale 0,015 mm, então esta amplitude cobre 261 posições distintas por
 * eixo — praticamente toda a entropia dos 256 valores de um byte.
 */
export const RACK_SHIFT: Fixed = F.from(0.002)

/**
 * Deslocamento individual de cada bola.
 *
 * NÃO PODE PASSAR DE METADE DA FOLGA DO RACK. As bolas nascem com 0,503 mm
 * entre elas; se duas vizinhas se aproximarem mais que isso, nascem
 * sobrepostas e a simulação dispara colisões antes da primeira tacada.
 *
 * Duas coisas tornam a conta pior do que parece, e as duas já morderam:
 *
 *   - o deslocamento é por EIXO, então na diagonal ele vale amplitude·√2
 *   - o `floor` do mapeamento estende a faixa para −11..+9 unidades quando a
 *     amplitude nominal é 10, porque arredondar para baixo nunca encurta o
 *     lado negativo
 *
 * Com 0,1 mm a faixa real é −8..+6 unidades, a aproximação máxima entre duas
 * vizinhas é ~23 unidades e a folga disponível é ~32. Sobra margem de verdade.
 *
 * A v1 usava ±0,2 mm e ignorava as duas correções: sobrepunha bolas em quase
 * todo rack, sem nada acusando — a simulação simplesmente começava resolvendo
 * colisões que não deviam existir. Não havia teste de sobreposição. Agora há.
 *
 * A entropia da quebra não depende mais deste número: ela mora em
 * `RACK_SHIFT`, que desloca o triângulo inteiro sem alterar as distâncias.
 */
export const BALL_JITTER: Fixed = F.from(0.0001)

/**
 * Deriva o jitter de um seed de 32 bytes (o `hash(nonceA ‖ nonceB)`).
 *
 * Sem isto a quebra vira um problema resolvido: alguém acha o vetor ótimo e
 * ganha toda partida em que quebra.
 *
 * Duas camadas, e a divisão não é estética — é o que permite amplitude grande
 * sem quebrar o triângulo:
 *
 *   - o RACK INTEIRO desliza até ±2 mm, o que move onde a branca acerta a bola
 *     da frente e muda a quebra por completo. Como todas as bolas andam junto,
 *     a distância entre elas não muda e nenhuma nasce sobreposta.
 *   - cada BOLA ainda desloca ±0,2 mm por conta própria, o mínimo para o
 *     aglomerado não ser idêntico a cada rack.
 *
 * A tentativa óbvia — dar ±2 mm a cada bola — foi medida e sobrepõe bolas em
 * 100% dos seeds, até 3,75 mm. Alargar o rack para acomodar isso deixaria as
 * bolas soltas, e num rack real elas estão coladas: é assim que a energia da
 * quebra atravessa o aglomerado.
 */
export function jitterFromSeed(seed: Uint8Array): Fixed[] {
  const valores: Fixed[] = []

  // Dois bytes dedicados ao deslize do rack, fora do laço das bolas, para o
  // valor não depender de quantas bolas a modalidade usa.
  const deslizeX = espalhar(seed[30] ?? 0, RACK_SHIFT)
  const deslizeY = espalhar(seed[31] ?? 0, RACK_SHIFT)

  for (let i = 0; i < 30; i++) {
    const byte = seed[i % seed.length] ?? 0
    const proprio = espalhar(byte, BALL_JITTER)
    valores.push(proprio + (i % 2 === 0 ? deslizeX : deslizeY))
  }
  return valores
}

/** Mapeia 0..255 para -amplitude..+amplitude, com arredondamento uniforme. */
const espalhar = (byte: number, amplitude: Fixed): Fixed =>
  Math.floor((amplitude * 2 * (byte - 128)) / 255)

export type ShotResult = {
  events: CollisionEvent[]
  /** Atributos efetivamente aplicados, já limitados. Vão para o replay. */
  cue: CueParams
  /** Ângulo depois de encaixado na grade do taco — o que de fato foi jogado. */
  aimedAngle: Fixed
  stateHash: string
  eventsHash: string
  /**
   * Quantos passos de `DT` a mesa levou para parar — o tempo das bolas rolando
   * na tela. Opcional porque os motores congelados não o informam; o atual
   * sempre informa.
   */
  passos?: number
}

/**
 * Aplica a tacada à branca, SEM simular.
 *
 * Existe separado porque o cliente precisa animar: ele dá o impulso e depois
 * avança quadro a quadro, enquanto o servidor simula tudo de uma vez. Os dois
 * partem exatamente do mesmo estado inicial, então chegam ao mesmo resultado —
 * que é o que permite a predição local sem divergir.
 */
export function beginShot(state: TableState, shot: Shot): { cue: CueParams; aimedAngle: Fixed } {
  const branca = state.balls.find((b) => b.id === CUE_BALL)
  if (!branca) throw new Error('Mesa sem bola branca.')
  if (branca.pocketed) throw new Error('A branca está encaçapada; reposicione antes de jogar.')

  // Prende os atributos mesmo vindos do servidor: um bug em outro lugar não
  // pode virar taco com o dobro da força.
  const taco = clampCue(shot.cue ?? DEFAULT_CUE)

  // A mira cai na grade do taco. Determinístico: mesmo ângulo, mesmo taco,
  // mesmo resultado — sem sorteio, o replay continua reproduzindo a partida.
  const aimedAngle = quantizeAim(shot.intent.angle, taco)

  const forca = F.clamp(shot.intent.power, 0, F.ONE)
  const potencia = shotPowerBpsFor(taco, shot.isBreak === true)
  const velocidade = F.mul(F.mul(MAX_SHOT_SPEED, forca), bpsToFixed(potencia))
  const direcao = V.fromAngle(aimedAngle)

  V.set(branca.velocity, F.mul(direcao.x, velocidade), F.mul(direcao.y, velocidade))

  /*
   * O ponto de contato do taco vira ROTAÇÃO DE VERDADE.
   *
   * `intent.spin.y` é onde o taco bate na vertical: acima do centro imprime
   * rotação para a frente (a bola corre depois de bater), abaixo imprime
   * rotação para trás (ela recua). `intent.spin.x` é o inglês, que gira a bola
   * em torno do eixo vertical.
   *
   * O que muda da v2: aqui a rotação é a velocidade de superfície INICIAL, e a
   * partir daí quem manda é o atrito com o pano. Antes, este número era um
   * impulso guardado para ser gasto no próximo impacto — a bola não girava, ela
   * carregava um crédito. É por isso que não havia curva no percurso.
   *
   * A rotação da FRENTE é alinhada com a direção da tacada; a lateral vai para
   * `spinZ`, que age na tabela e no throw.
   */
  const contato = shot.intent.spin
  if (contato) {
    /*
     * Quanta rotação o taco consegue IMPRIMIR.
     *
     * Dois atributos entram, e por motivos diferentes: `spinBps` é o alcance do
     * taco (quão longe do centro dá para bater sem falhar), e a aderência do
     * couro é quanto da tacada vira giro em vez de escorregar no contato.
     *
     * Na v2 a aderência governava o DECAIMENTO do efeito ao longo da tacada.
     * Na v3 quem faz isso é o atrito do pano — que é propriedade da mesa, não
     * do taco —, então ela migrou para onde faz sentido: o instante da tacada.
     * Sem isso, o atributo teria virado enfeite, e um taco melhor não jogaria
     * diferente.
     */
    const aderencia = F.mul(bpsToFixed(taco.spinBps), spinDecayFor(taco))

    /*
     * O VERTICAL escala com a velocidade da tacada; o lateral, não.
     *
     * Recuo e corrida são uma disputa entre o giro e o rolamento natural, e o
     * rolamento natural É a velocidade — então o giro tem de ser fração dela,
     * senão a mesma posição do taco vale coisas diferentes a cada força. Ver
     * `SPIN_PER_RADIUS`. O lateral segue em m/s absolutos porque a tabela e o
     * throw foram calibrados assim contra a referência.
     */
    const autoridadeVertical = F.mul(F.mul(SPIN_PER_RADIUS, velocidade), aderencia)
    const autoridade = F.mul(F.mul(MAX_SPIN, forca), aderencia)
    const vertical = F.mul(F.clamp(contato.y, -MISCUE_LIMIT, MISCUE_LIMIT), autoridadeVertical)

    /*
     * A rotação da frente é PERPENDICULAR à direção da tacada, não alinhada.
     *
     * Pela condição de rolagem, uma bola indo em `d` com velocidade `v` rola
     * naturalmente quando `s = v·(−d.y, d.x)`. Então é nessa direção que o topo
     * e o recuo se somam ou se subtraem do rolamento natural — alinhar `s` com
     * `d` daria uma rotação de eixo errado, que o pano converte em curva
     * lateral em vez de corrida ou recuo.
     */
    V.set(branca.spin, F.mul(-direcao.y, vertical), F.mul(direcao.x, vertical))
    branca.spinZ = F.mul(F.clamp(contato.x, -F.ONE, F.ONE), autoridade)
  } else {
    V.set(branca.spin, 0, 0)
    branca.spinZ = 0
  }

  // Tacada nova: a bola sai patinando, sempre. Rolagem é algo que ela CONQUISTA
  // depois de o pano alinhar rotação e velocidade.
  branca.rolling = false

  return { cue: taco, aimedAngle }
}

/**
 * Aplica a tacada e simula até tudo parar.
 *
 * Muta o estado recebido — quem precisar do anterior guarda um `cloneState`.
 */
export function applyShot(state: TableState, shot: Shot): ShotResult {
  const { cue, aimedAngle } = beginShot(state, shot)
  const { events, passos } = simularContando(state)

  return {
    events,
    cue,
    aimedAngle,
    stateHash: hashState(state),
    eventsHash: hashEvents(events),
    passos,
  }
}
